/**
 * Single source of truth for run-status semantics.
 *
 * The BE status string is mapped onto the closed `RunStatus` set so terminal
 * detection (which stops polling) and the timeline have one definition. Observed
 * BE spellings vary (`CREATED`, `SUBMITTED`, `Succeeded`, `Failed`, …) so the
 * mapping is case-insensitive and tolerant.
 */
import type { RunStatus } from "../types";

export const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
]);

const STATUS_ALIASES: Record<string, RunStatus> = {
  created: "SUBMITTED",
  submitted: "SUBMITTED",
  pending: "SUBMITTED",
  accepted: "SUBMITTED",
  queued: "SUBMITTED",
  running: "RUNNING",
  in_progress: "RUNNING",
  inprogress: "RUNNING",
  processing: "RUNNING",
  started: "RUNNING",
  suspended: "SUSPENDED",
  succeeded: "SUCCEEDED",
  success: "SUCCEEDED",
  successful: "SUCCEEDED",
  completed: "SUCCEEDED",
  complete: "SUCCEEDED",
  done: "SUCCEEDED",
  failed: "FAILED",
  failure: "FAILED",
  error: "FAILED",
  errored: "FAILED",
  cancelled: "CANCELLED",
  canceled: "CANCELLED",
  aborted: "CANCELLED",
};

export const normalizeRunStatus = (raw?: string | null): RunStatus => {
  if (!raw) {
    return "UNKNOWN";
  }

  return STATUS_ALIASES[raw.trim().toLowerCase()] ?? "UNKNOWN";
};

export const isTerminal = (raw?: string | null): boolean =>
  TERMINAL_STATUSES.has(normalizeRunStatus(raw));

/**
 * Whether a terminal run still warrants a few more polls: the BE can write the
 * terminal status before it persists `errorMessage`, so a FAILED poll can arrive
 * without the message a later read has. Stopping at the first terminal sighting
 * loses the failure reason — the run page freezes without it, and an awaited run
 * throws a bare `Workflow "x" FAILED.` instead of what actually went wrong.
 *
 * Poll on while the failure is message-less and its own `updatedAt` is fresh —
 * the freshness window self-bounds the extra polling even if the BE never
 * backfills a message.
 */
export const TERMINAL_MESSAGE_GRACE_MS = 30_000;

export const shouldAwaitErrorMessage = (run: {
  status?: string;
  errorMessage?: string;
  updatedAt?: string;
}): boolean => {
  if (normalizeRunStatus(run.status) === "SUCCEEDED" || run.errorMessage) {
    return false;
  }

  const updatedAtMs = Date.parse(run.updatedAt ?? "");

  return Number.isFinite(updatedAtMs) && Date.now() - updatedAtMs < TERMINAL_MESSAGE_GRACE_MS;
};
